import React, { useState, useEffect } from "react"
import { useMutation, useQuery } from "@apollo/client"
import { useLocation, useParams, useNavigate } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import Button from "../../components/Button/Button"
import { REGISTER } from "../../graphql/mutations"
import { VALIDATE_SIGNUP_LINK } from "../../graphql/queries"
import { loginStart, loginSuccess, loginFailure, clearError } from "../../store/slices/authSlice"
import { setCurrentUser } from "../../store/slices/mateSlice"
import "./Signup.css"

const Signup = ({ onClose, onSignupSuccess }) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { loading, error } = useSelector((state) => state.auth)
  const location = useLocation()
  const params = useParams()
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [name, setName] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [signupToken, setSignupToken] = useState("")
  const [tokenValid, setTokenValid] = useState(null)
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated)

  // Hvis brugeren allerede er logget ind, naviger væk fra signup siden
  useEffect(() => {
    if (isAuthenticated && !onClose) {
      // Kun naviger hvis vi er på signup route (ikke modal)
      navigate('/')
    }
  }, [isAuthenticated, navigate, onClose])

  // Hent signup token fra URL parameter eller URL sti
  useEffect(() => {
    // Først tjek URL sti parameter (nye links)
    if (params.token) {
      setSignupToken(params.token)
    } else {
      // Ellers tjek query parameter (gamle links)
      const urlParams = new URLSearchParams(location.search)
      const token = urlParams.get('token')
      if (token) {
        setSignupToken(token)
      }
    }
  }, [params.token, location.search])

  // Valider signup token hvis det findes
  const { loading: tokenLoading } = useQuery(VALIDATE_SIGNUP_LINK, {
    variables: { token: signupToken },
    skip: !signupToken,
    onCompleted: (data) => {
      if (data.validateSignupLink && data.validateSignupLink.isValid === true) {
        setTokenValid(true)
      } else {
        setTokenValid(false)
        dispatch(loginFailure("Ugyldigt eller udløbet signup link"))
      }
    },
    onError: (error) => {
      setTokenValid(false)
      dispatch(loginFailure("Ugyldigt signup link:" + error.message))
    }
  })

  const [registerMutation] = useMutation(REGISTER, {
    onCompleted: (data) => {
      // Dispatch auth success (kun token) - automatisk login
      dispatch(loginSuccess({
        token: data.register.token
      }))
      
      // Dispatch user data til mate slice (kommer fra backend)
      dispatch(setCurrentUser(data.register.mate))
      
      // Kald success callback hvis den findes
      if (onSignupSuccess) {
        onSignupSuccess(data.register.mate)
      }

      // Luk modal eller naviger væk fra signup siden
      if (onClose) {
        onClose()
      } else {
        // Hvis vi er på signup route, naviger til hjemmesiden efter vellykket registrering
        navigate('/')
      }
    },
    onError: (error) => {
      dispatch(loginFailure(error.message || "Registrering fejlede. Prøv igen."))
    }
  })

  const handleSubmit = async (e) => {
    e.preventDefault()
    dispatch(clearError())
    
    // Valider at passwords matcher
    if (password !== confirmPassword) {
      dispatch(loginFailure("Passwords matcher ikke"))
      return
    }
    
    // Hvis der er et signup token, valider at det er gyldigt
    if (signupToken && tokenValid !== true) {
      dispatch(loginFailure("Ugyldigt signup link"))
      return
    }
    
    dispatch(loginStart())
    
    try {
      await registerMutation({
        variables: {
          email: email,
          password: password,
          name: name,
          signupToken: signupToken || null,
          holdInvite: null
        }
      })
    } catch (error) {
      dispatch(loginFailure("Registrering fejlede. Prøv igen."))
    }
  }

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword)
  }

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword)
  }

  // Luk modal hvis man klikker på baggrunden
  const handleBackgroundClick = (e) => {
    if (e.target.classList.contains("signup-background") && onClose) {
      onClose()
    }
  }

  return (
    <div className="signup-background" onClick={handleBackgroundClick}>
      <div className="signup-container">
        <h2 className="signup-title">Opret Bruger</h2>
        {tokenLoading && (
          <div className="loading-message" style={{ color: 'blue', marginBottom: '10px', textAlign: 'center' }}>
            Validerer signup link...
          </div>
        )}
        {error && (
          <div className="error-message" style={{ color: 'red', marginBottom: '10px', textAlign: 'center' }}>
            {error}
          </div>
        )}
        <form className="signup-form" onSubmit={handleSubmit}>
          {signupToken && (
            <div className="input-container">
              <input 
                type="text" 
                placeholder="Navn" 
                value={name} 
                onChange={e => setName(e.target.value)} 
                required 
                disabled={loading}
              />
            </div>
          )}
          
          <div className="input-container">
            <input 
              type="email" 
              placeholder="Email" 
              value={email} 
              onChange={e => setEmail(e.target.value)} 
              required 
              disabled={loading}
            />
          </div>
          
          <div className="input-container">
            <input 
              type={showPassword ? "text" : "password"} 
              placeholder="Password" 
              value={password} 
              onChange={e => setPassword(e.target.value)} 
              required 
              disabled={loading}
            />
            <button 
              type="button" 
              className="password-toggle" 
              onClick={togglePasswordVisibility}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                {showPassword ? (
                  <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" fill="currentColor"/>
                ) : (
                  <path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z" fill="currentColor"/>
                )}
              </svg>
            </button>
          </div>
          
          <div className="input-container">
            <input 
              type={showConfirmPassword ? "text" : "password"} 
              placeholder="Bekræft password" 
              value={confirmPassword} 
              onChange={e => setConfirmPassword(e.target.value)} 
              required 
              disabled={loading}
            />
            <button 
              type="button" 
              className="password-toggle" 
              onClick={toggleConfirmPasswordVisibility}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                {showConfirmPassword ? (
                  <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" fill="currentColor"/>
                ) : (
                  <path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z" fill="currentColor"/>
                )}
              </svg>
            </button>
          </div>
          
          <Button 
            title={loading ? "Opretter bruger..." : "Opret Bruger"} 
            onClick={handleSubmit} 
            variant="contained" 
            color="secondary"
            style={{ width: '100%', marginTop: '8px' }}
            disabled={loading || tokenLoading || (signupToken && tokenValid !== true)}
          />
        </form>
      </div>
    </div>
  )
}

export default Signup
